/**
 * Aura — Incorporation chatflow (SG, Aura Partners 문서셋).
 * Studio: title → L_INCORPORATIONS 클라이언트 선택(선택) → confirm + live document package.
 * ⚠ 배선(등록)은 보류 — taskCodeMatch 는 실제 코드 확정 시 채운다.
 */
import type { ConversationMessage } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

export const auraIncorporationFlow: ChatFlow = {
  id: 'aura-incorporation',
  // 배선 보류: 실제 task code 확정 시 채운다 (예: ['B32109'] 또는 Aura 전용 코드)
  taskCodeMatch: [],
  categoryMatch: ['aura incorporation', 'aura'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to **Aura Incorporation**.\n\n` +
          `This preset prepares a live document package` +
          (docCount > 0 ? ` (${docCount} template(s) linked)` : '') +
          `: Constitution, Engagement Letter, Form 45 / 45B, Controller Notices, ` +
          `Corporate Shareholder Profile, Client Acceptance, and ACRA Risk Assessment.\n\n` +
          `Fill documents on the right. Click **✨** on a name to look up a **Contact** ` +
          `(address, DOB, ID, nationality auto-fill). Use **📍** for addresses, and the ` +
          `briefcase icon on **Business Activity** to search **SSIC**.\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage:
        'Optionally link a **completed incorporation** (from T-Link / L_INCORPORATIONS) for auto-fill, or skip and fill manually.',
      widget: 'incorporation-doc-entity',
      widgetConfig: {
        targetVarKey: '_tlinkEntity',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Ready. Complete the Incorporation (Pre) documents on the right — add Form 45 / Notice copies as needed. ' +
        'Click **Generate** when ready.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'officer-role',
      aiMessage: 'Please select a role for the person you want to look up.',
      widget: 'officer-role-buttons',
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage: 'Person details loaded. The form has been pre-filled.',
      widget: 'officer-field-picker',
      awaitEvent: true,
    },
    {
      id: 'address-input',
      aiMessage: 'Enter the address below.',
      widget: 'address-input',
      awaitEvent: true,
    },
    {
      id: 'pick-ssic',
      aiMessage: 'Search and select an **SSIC / Business Activity**.',
      widget: 'business-code',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (
      (eventName === 'value-submit' || eventName === 'text-input-submit') &&
      context.currentStepId === 'input-title'
    ) {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { _taskTitle: String(title).trim(), taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (
      (eventName === 'value-submit' ||
        eventName === 'skip' ||
        eventName === 'entity-skip') &&
      (context.currentStepId === 'select-entity' || context.currentStepId === 'pick-company')
    ) {
      return {
        messages: [
          {
            role: 'user',
            content: 'Skipped incorporation linking. Fill documents manually.',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd: '',
          icpCd: '',
          entityName: '',
          company_name: '',
          registrationNo: '',
          tlinkData: null,
          entitySkipped: true,
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'setup-entity-autofill' || eventName === 'entity-field-select') {
      let parsed: any = payload;
      if (typeof payload === 'string') {
        try {
          parsed = JSON.parse(payload);
        } catch {
          parsed = { _entityName: payload };
        }
      }
      const entityCd = String(
        parsed?._icpCd || parsed?.icpCd || parsed?.entityCd || '',
      ).trim();
      const entityName = String(
        parsed?._entityName ||
          parsed?.entityName ||
          parsed?.companyName ||
          'Entity',
      ).trim();
      const isIncPick = !!(parsed?._icpCd || parsed?.icpCd);
      if (eventName === 'entity-field-select' && !isIncPick) return {};
      if (!entityCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Linked incorporation: **${entityName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd,
          icpCd: entityCd,
          entityName,
          company_name: entityName,
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const key = String(ctx.key || '');
      const scopedKey = String(ctx.scopedKey || key || '').trim();

      // SSIC / Business Activity ✨ → SSIC 검색 위젯
      if (
        ctx.lookupType === 'ssic' ||
        ctx.lookupType === 'msic' ||
        /business_activity$/.test(key) ||
        /(^|_)ssic$/.test(key)
      ) {
        return {
          messages: [
            {
              role: 'user',
              content: 'Look up **SSIC / Business Activity**…',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          formUpdates: { _ssicTargetKey: scopedKey || key },
          nextStepId: 'pick-ssic',
          nextStepWidgetData: { targetVarKey: scopedKey || key },
        };
      }

      if (
        key === 'company_name' ||
        key === 'entity_name' ||
        key === 'proposed_company_name' ||
        ctx.lookupType === 'incorporation' ||
        (ctx.fieldGroup === 'sg-incorporation-pre' && !ctx.defaultRole && ctx.lookupType !== 'ssic')
      ) {
        return {
          messages: [
            {
              role: 'user',
              content: 'Select a **company** from incorporations…',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'select-entity',
        };
      }
      return {
        messages: [
          {
            role: 'user',
            content: `Looking up person for **${key || 'field'}**…`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          _personTargetKey: key,
          _personFieldGroup: ctx.fieldGroup || 'sg-incorporation-pre',
        },
        nextStepId: ctx.defaultRole ? 'officer-list' : 'officer-role',
        // role 은 selectedRole 과 동일 별칭 — 위젯 호환용으로 함께 전달 (타입엔 없어 캐스트)
        nextStepWidgetData: {
          selectedRole: ctx.defaultRole || undefined,
          role: ctx.defaultRole || undefined,
          // CONTACT 는 L_ENTITY 불필요 — icpCd 를 entityCd 로 넣지 않음
          entityCd:
            String(ctx.defaultRole || '').toUpperCase() === 'CONTACT'
              ? ''
              : ctx.entityCd || context.formData?.entityCd || '',
        } as any,
      };
    }

    // SSIC 선택
    if (eventName === 'value-submit' && context.currentStepId === 'pick-ssic') {
      let code = '';
      let name = '';
      if (payload && typeof payload === 'object') {
        code = String(payload.code || '').trim();
        name = String(payload.name || payload.value || '').trim();
      } else {
        name = String(payload || '').trim();
      }
      if (!name && !code) return {};
      const target = String(context.formData._ssicTargetKey || 'entity_business_activity').trim();
      const display = code && name ? `${code} - ${name}` : name || code;
      return {
        messages: [
          {
            role: 'user',
            content: `Business Activity: **${display}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          [target]: display,
          _ssicTargetKey: target,
          _ssicDisplay: display,
          _ssicCode: code,
          _ssicName: name,
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-address-input') {
      const key = String(payload?.key || payload || '').trim();
      return {
        messages: [
          {
            role: 'user',
            content: key ? `Enter address for **${key}**.` : 'Enter an address.',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: { _addressTargetKey: key },
        nextStepId: 'address-input',
      };
    }

    if (eventName === 'officer-role-select') {
      const role = typeof payload === 'string' ? payload : payload?.role || payload?.value;
      if (!role) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Role: **${role}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-list',
        nextStepWidgetData: {
          selectedRole: role,
          role,
          entityCd:
            String(role || '').toUpperCase() === 'CONTACT'
              ? ''
              : context.formData?.entityCd || '',
        } as any,
      };
    }

    if (eventName === 'officer-select' || eventName === 'person-select') {
      return {
        messages: [
          {
            role: 'user',
            content: 'Person selected.',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'officer-detail',
        formUpdates: { _selectedOfficer: payload },
      };
    }

    if (eventName === 'officer-field-apply' || eventName === 'value-submit') {
      if (context.currentStepId === 'officer-detail' || context.currentStepId === 'address-input') {
        return {
          messages: [
            {
              role: 'user',
              content: 'Applied to the document.',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'confirm',
        };
      }
    }

    return {};
  },
};
